import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { MARKETPLACE_SEO, buildCanonical } from "../../constants/siteSeo";
import { usePageSeo } from "../../hooks/usePageSeo";
import { getMarketplaceProductBySlug } from "../../services/marketplaceApi";
import { trackProductViewed } from "../../services/analyticsClient";
import { MARKETPLACE_FEATURED_BANNER, resolveProductVisual } from "../../data/marketplaceVisualAssets";
import "./marketplace.css";

export default function MarketplaceProductPage() {
  const { productSlug } = useParams();
  const [search] = useSearchParams();
  const storeSlug = search.get("storeSlug") || "";

  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  const pageSeo = useMemo(() => {
    const product = data?.product;
    const title = product?.title || "Product preview";
    const path = `/marketplace/product/${productSlug}`;
    return {
      ...MARKETPLACE_SEO,
      title: `${title} | Treydora Marketplace`,
      description:
        product?.shortDescription ||
        product?.description ||
        MARKETPLACE_SEO.description,
      canonicalUrl: buildCanonical(path),
      ogUrl: buildCanonical(path),
    };
  }, [data?.product, productSlug]);

  usePageSeo(data?.product ? pageSeo : MARKETPLACE_SEO);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await getMarketplaceProductBySlug(productSlug, storeSlug);
        if (!cancelled) setData(d);
      } catch (e) {
        if (!cancelled) setErr(e?.message || "Product not found.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [productSlug, storeSlug]);

  useEffect(() => {
    if (!data?.product) return;
    trackProductViewed("marketplace_product", productSlug, {
      storeSlug: storeSlug || undefined,
      title: data.product.title || undefined,
    });
  }, [data?.product, productSlug, storeSlug]);

  if (err) {
    return (
      <div className="mp-page mp-atmosphere">
        <p className="mp-banner mp-banner--warn">{err}</p>
        <Link to="/marketplace" className="mp-link">
          ← Marketplace
        </Link>
      </div>
    );
  }

  if (!data?.product) {
    return (
      <div className="mp-page mp-atmosphere">
        <p className="mp-muted">Loading product…</p>
      </div>
    );
  }

  const { product, store, preview } = data;
  const images =
    [product.thumbnailUrl, ...(Array.isArray(product.previewImages) ? product.previewImages : [])].filter(Boolean);
  const uniqueImages = [...new Set(images)];
  const heroVisual = uniqueImages[0] || resolveProductVisual(product);

  return (
    <div className="mp-page mp-page--product mp-atmosphere">
      <section className="mp-product-hero-slab" aria-hidden>
        <img className="mp-product-hero-slab__img" src={heroVisual} alt="" fetchPriority="high" decoding="async" />
        <img className="mp-product-hero-slab__accent" src={MARKETPLACE_FEATURED_BANNER} alt="" loading="lazy" decoding="async" />
        <div className="mp-product-hero-slab__veil" />
      </section>

      <div className="mp-preview-ribbon mp-preview-ribbon--tight mp-preview-ribbon--elevated mp-preview-ribbon--product" role="status">
        <span className="mp-preview-ribbon__badge">Live listing</span>
        <span>
          <strong>Listed on Treydora.com.</strong> Browse product details and return to the storefront for more from this
          seller.
        </span>
      </div>
      {preview ? (
        <div className="mp-banner mp-banner--warn">
          Preview — this listing is not published yet. Public buyers will not see it until TreydoraED approves it.
        </div>
      ) : null}

      <div className="mp-product-shell mp-glass">
        <p className="mp-hero__eyebrow mp-hero__eyebrow--gold">
          {store ? (
            <Link className="mp-link" to={`/marketplace/store/${encodeURIComponent(store.storeSlug)}`}>
              {store.storeName}
            </Link>
          ) : (
            "Oshar Marketplace"
          )}
        </p>
        <h1 className="mp-hub-hero__title mp-product-shell__title">{product.title}</h1>

        <div className="mp-product-arena">
          <div className="mp-product-arena__lore">
            {product.shortDescription ? <p className="mp-hub-hero__sub">{product.shortDescription}</p> : null}
            <p className="mp-muted mp-product-shell__body">
              {product.description || "Digital goods — delivery after purchase."}
            </p>

            {uniqueImages.length > 0 ? (
              <div className="mp-gallery mp-gallery--product">
                {uniqueImages.map((src) => (
                  <img key={src} src={src} alt="" loading="lazy" decoding="async" />
                ))}
              </div>
            ) : (
              <div className="mp-product-visual-fallback mp-product-visual-fallback--art">
                <img
                  className="mp-product-visual-fallback__art"
                  src={resolveProductVisual(product)}
                  alt=""
                  loading="lazy"
                  decoding="async"
                />
                <div className="mp-product-visual-fallback__grid" aria-hidden />
                <span className="mp-product-visual-fallback__label">Seller art pending · showing realm-matched visual</span>
              </div>
            )}
          </div>

          <aside className="mp-product-arena__sidebar" aria-label="Listing summary">
            <div className="mp-product-tile mp-glass">
              <p className="mp-product-tile__label">Display price</p>
              <p className="mp-product-tile__price-lg">${(Number(product.priceCents || 0) / 100).toFixed(2)} USD</p>
              <p className="mp-muted mp-product-shell__fine">Display only — not available for purchase.</p>
            </div>

            <div className="mp-product-tile mp-glass">
              <p className="mp-product-tile__label">Storefront</p>
              {store ? (
                <p className="mp-product-tile__store">
                  <Link className="mp-link" to={`/marketplace/store/${encodeURIComponent(store.storeSlug)}`}>
                    {store.storeName}
                  </Link>
                </p>
              ) : (
                <p className="mp-muted mp-product-tile__store">Parcel not linked in this response.</p>
              )}
              <dl className="mp-product-tile__dl">
                {product.category ? (
                  <div>
                    <dt className="mp-product-tile__dt">Category</dt>
                    <dd className="mp-product-tile__dd">{product.category}</dd>
                  </div>
                ) : null}
                {product.licenseType ? (
                  <div>
                    <dt className="mp-product-tile__dt">License / rights</dt>
                    <dd className="mp-product-tile__dd">{product.licenseType}</dd>
                  </div>
                ) : null}
                {product.deliveryNotes ? (
                  <div>
                    <dt className="mp-product-tile__dt">Delivery notes</dt>
                    <dd className="mp-product-tile__dd">{product.deliveryNotes}</dd>
                  </div>
                ) : null}
              </dl>
            </div>

            <div className="mp-commerce-offline mp-glass">
              <h2 className="mp-commerce-offline__title">Purchases not enabled</h2>
              <p className="mp-muted">
                Stripe card checkout and Treydorex are disconnected. Downloads and file delivery remain off until marketplace
                operations go live.
              </p>
              <div className="mp-actions mp-actions--stack">
                <button type="button" className="mp-btn mp-btn--primary" disabled title="Purchases are not enabled in this phase">
                  Browse Listing
                </button>
                <button type="button" className="mp-btn mp-btn--ghost" disabled title="Treydorex marketplace checkout is not enabled">
                  Treydorex — disabled
                </button>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <Link to="/marketplace" className="mp-link mp-back-link">
        ← Oshar Marketplace
      </Link>
    </div>
  );
}
